import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
// @mui
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

import Iconify from 'src/components/iconify';
import { PROGRAM_NAMES } from 'src/sections/off-campus/program-workflow';

import {
  SEEN_OPTIONS,
  VISIT_STATUS_OPTIONS,
  activeFilterCount,
  distinctValues,
} from './caseload';

// ----------------------------------------------------------------------

// The caseload table's search, grade chips and the rest of its filters (#612),
// on one row, with the less used ones under More filters. Every change goes up through onChange(partialFilters); the
// page keeps them in the URL.

const GRADES = [9, 10, 11, 12];
const PROGRESS_OPTIONS = [25, 50, 75, 100];
const CONTACT_OPTIONS = [14, 30, 60, 90];

const Choice = ({ label, value, onChange, options, anyLabel = 'Any' }) => (
  <FormControl size="small" fullWidth>
    <InputLabel>{label}</InputLabel>
    <Select label={label} value={value} onChange={(e) => onChange(e.target.value)}>
      <MenuItem value="">{anyLabel}</MenuItem>
      {options.map((o) => (
        <MenuItem key={o.value} value={o.value}>
          {o.label}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);

Choice.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.array.isRequired,
  anyLabel: PropTypes.string,
};

const CaseloadFilters = ({ rows, filters, onChange, onReset, gradeCounts, ai = false }) => {
  const [open, setOpen] = useState(false);
  const count = activeFilterCount(filters);
  // Everything but the search and grade chips, which are always showing
  const moreCount = count - (filters.q ? 1 : 0) - (filters.grades.length ? 1 : 0) - (filters.graduating ? 1 : 0);

  const pathways = useMemo(() => distinctValues(rows, (r) => r.pathway), [rows]);
  const interests = useMemo(() => distinctValues(rows, (r) => r.interests), [rows]);
  const industries = useMemo(() => distinctValues(rows, (r) => r.industry), [rows]);
  const opts = (list) => list.map((v) => ({ value: v, label: v }));

  const toggleGrade = (grade) =>
    onChange({ grades: filters.grades.includes(grade) ? filters.grades.filter((g) => g !== grade) : [...filters.grades, grade] });

  const flag = (key, label) => (
    <FormControlLabel
      control={<Checkbox size="small" checked={filters[key]} onChange={(e) => onChange({ [key]: e.target.checked })} />}
      label={label}
    />
  );

  return (
    <Stack spacing={2}>
      {/* Search, then the buttons for the rest; the grade chips get their own row */}
      <Stack direction="row" spacing={1.5} alignItems="center">
        <TextField
          size="small"
          placeholder="Search name or ASN"
          value={filters.q}
          onChange={(e) => onChange({ q: e.target.value })}
          sx={{ flex: 1, maxWidth: 360 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        />
        <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: 'auto !important', flexShrink: 0 }}>
          {count > 0 && (
            <Button size="small" color="inherit" onClick={onReset}>
              Clear
            </Button>
          )}
          <Badge color="primary" badgeContent={moreCount} invisible={!moreCount}>
            <Button
              size="small"
              variant={open ? 'soft' : 'outlined'}
              color="inherit"
              onClick={() => setOpen((o) => !o)}
              startIcon={<Iconify icon="solar:filter-bold" width={18} />}
              aria-expanded={open}
            >
              More filters
            </Button>
          </Badge>
        </Stack>
      </Stack>

      <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center">
        <Chip
          label={`All grades (${gradeCounts.all})`}
          color={!filters.grades.length && !filters.graduating ? 'primary' : 'default'}
          variant={!filters.grades.length && !filters.graduating ? 'filled' : 'outlined'}
          onClick={() => onChange({ grades: [], graduating: false })}
        />
        {GRADES.map((g) => (
          <Chip
            key={g}
            label={`Grade ${g} (${gradeCounts[g] || 0})`}
            color={filters.grades.includes(g) ? 'primary' : 'default'}
            variant={filters.grades.includes(g) ? 'filled' : 'outlined'}
            onClick={() => toggleGrade(g)}
          />
        ))}
        <Chip
          label={`Graduating (${gradeCounts.graduating || 0})`}
          color={filters.graduating ? 'primary' : 'default'}
          variant={filters.graduating ? 'filled' : 'outlined'}
          onClick={() => onChange({ graduating: !filters.graduating })}
        />
      </Stack>

      <Collapse in={open} unmountOnExit>
        <Box sx={{ p: 2, borderRadius: 1.5, bgcolor: 'background.neutral' }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Choice label="Last visit" value={filters.seen} onChange={(seen) => onChange({ seen })} options={SEEN_OPTIONS} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Choice
                label="No contact in"
                value={filters.contact}
                onChange={(contact) => onChange({ contact })}
                options={CONTACT_OPTIONS.map((n) => ({ value: String(n), label: `${n}+ days` }))}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl size="small" fullWidth>
                <InputLabel>Next visit</InputLabel>
                <Select
                  multiple
                  label="Next visit"
                  value={filters.visit}
                  onChange={(e) => onChange({ visit: e.target.value })}
                  renderValue={(selected) => VISIT_STATUS_OPTIONS.filter((o) => selected.includes(o.value)).map((o) => o.label).join(', ')}
                >
                  {VISIT_STATUS_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      <Checkbox size="small" checked={filters.visit.includes(o.value)} />
                      <ListItemText primary={o.label} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Choice
                label="Off-campus"
                value={filters.program}
                onChange={(program) => onChange({ program })}
                anyLabel="Any student"
                options={[{ value: 'any', label: 'In any program' }, ...Object.entries(PROGRAM_NAMES).map(([value, label]) => ({ value, label }))]}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Choice
                label="To-dos"
                value={filters.todos}
                onChange={(todos) => onChange({ todos })}
                options={[
                  { value: 'open', label: 'Has open to-dos' },
                  { value: 'overdue', label: 'Has overdue to-dos' },
                ]}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Choice
                label="Checklist progress"
                value={filters.progress}
                onChange={(progress) => onChange({ progress })}
                options={PROGRESS_OPTIONS.map((n) => ({ value: String(n), label: `Under ${n}%` }))}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Choice
                label="Learning pathway"
                value={filters.pathway}
                onChange={(pathway) => onChange({ pathway })}
                options={[{ value: 'none', label: 'No pathway set' }, ...opts(pathways)]}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Choice
                label="Prerequisite not done"
                value={filters.prereq}
                onChange={(prereq) => onChange({ prereq })}
                options={[
                  { value: 'HCS3000', label: 'HCS 3000' },
                  { value: 'AGR3000', label: 'AGR 3000' },
                ]}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Choice label="Interest" value={filters.interest} onChange={(interest) => onChange({ interest })} options={opts(interests)} />
            </Grid>
            {/* Suggested industry and flags come from the Careers AI features: only while those are on */}
            {ai && (
              <Grid item xs={12} sm={6} md={3}>
                <Choice label="Suggested industry" value={filters.industry} onChange={(industry) => onChange({ industry })} options={opts(industries)} />
              </Grid>
            )}
            <Grid item xs={12}>
              <Stack direction="row" flexWrap="wrap" columnGap={3}>
                {flag('referral', 'Referral pending')}
                {flag('placement', 'Placement active')}
                {flag('agreement', 'Agreement not signed')}
                {flag('ending', 'Placement ends within 30 days')}
                {ai && flag('flags', 'Has flags')}
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </Collapse>
    </Stack>
  );
};

CaseloadFilters.propTypes = {
  ai: PropTypes.bool,
  rows: PropTypes.array.isRequired,
  filters: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  gradeCounts: PropTypes.object.isRequired,
};

export default CaseloadFilters;
